import React, { useState } from "react";
import { MediumHeader } from "@narvar/nth-bit-header";
import { useTranslations } from "@narvar/nth-hook-translations";
import LanguageKeys from "../../ReviewSummary/consts/language_en_US";
import {
  LanguageProvider,
  useReturnsConfig,
} from "@narvar/nth-kit-returns-headless";
import { ErrorBanner, Language } from "../../ReviewSummary/components";
import SelectList from "../../ReviewSummary/components/SelectList/SelectList";

const ZERO_VALUE = 0;

const tempLanguageKeys = {
  returnsReasonsQuantityAllError: LanguageKeys.returnsReasonsQuantityAllError,
  returnsReasonsQuantityAllTitle: LanguageKeys.returnsReasonsQuantityAllTitle,
  returnsReasonsQuantityAllTitleHipaa:
    LanguageKeys.returnsReasonsQuantityAllTitleHipaa,
  returnsReasonsQuantityAllYesLabel:
    LanguageKeys.returnsReasonsQuantityAllYesLabel,
  returnsReasonsQuantityAllNoLabel:
    LanguageKeys.returnsReasonsQuantityAllNoLabel,
};

const ReturnReasonsQuantityAll = ({
  onChange,
  totalQuantity,
}: {
  onChange: (e: number) => void;
  totalQuantity: number | null;
}) => {
  const { isHipaaCompliant } = useReturnsConfig();
  const [selection, setSelection] = useState<string | null>(null);
  const [isErrorShown, setErrorShown] = useState<boolean>(false);

  const {
    returnsReasonsQuantityAllError,
    returnsReasonsQuantityAllTitle,
    returnsReasonsQuantityAllTitleHipaa,
    returnsReasonsQuantityAllYesLabel,
    returnsReasonsQuantityAllNoLabel,
  } = useTranslations();

  if (!totalQuantity) return null;

  const handleChange = (e: string) => {
    // set the selected value
    setSelection(e);

    // make sure the value is of type number
    const valueAsNumber = Number.parseInt(e, 10);

    // if the value is 0, we show the error and return early
    if (valueAsNumber === ZERO_VALUE) {
      setErrorShown(true);
      return;
    } else {
      setErrorShown(false);
    }

    // if the value is not zero, we set the quantity and proceed to the next step via the parent component's onChange
    onChange(Number.parseInt(e, 10));
  };

  const langKeySuffix = isHipaaCompliant ? "Hipaa" : "";

  return (
    <LanguageProvider
      language={{
        returnsReasonsQuantityAllError:
          returnsReasonsQuantityAllError ??
          tempLanguageKeys.returnsReasonsQuantityAllError,
        returnsReasonsQuantityAllTitle:
          returnsReasonsQuantityAllTitle ??
          tempLanguageKeys.returnsReasonsQuantityAllTitle,
        returnsReasonsQuantityAllTitleHipaa:
          returnsReasonsQuantityAllTitleHipaa ??
          tempLanguageKeys.returnsReasonsQuantityAllTitleHipaa,
      }}>
      {isErrorShown && (
        <ErrorBanner>
          <Language
            langKey="returnsReasonsQuantityAllError"
            defaultString={tempLanguageKeys.returnsReasonsQuantityAllError}
          />
        </ErrorBanner>
      )}
      <MediumHeader>
        <Language
          langKey={`returnsReasonsQuantityAllTitle${langKeySuffix}`}
          defaultString={
            isHipaaCompliant
              ? tempLanguageKeys.returnsReasonsQuantityAllTitleHipaa
              : tempLanguageKeys.returnsReasonsQuantityAllTitle
          }
        />
      </MediumHeader>
      <SelectList
        onChange={handleChange}
        options={[
          {
            value: totalQuantity.toString(),
            label:
              returnsReasonsQuantityAllYesLabel ??
              tempLanguageKeys.returnsReasonsQuantityAllYesLabel,
          },
          {
            value: ZERO_VALUE.toString(),
            label:
              returnsReasonsQuantityAllNoLabel ??
              tempLanguageKeys.returnsReasonsQuantityAllNoLabel,
          },
        ]}
        selection={selection}
      />
    </LanguageProvider>
  );
};

export default ReturnReasonsQuantityAll;
